// Considered: SegmentedControlAtom, ForkPolicyRadioGroupAtom — rejected because the segmented
// control strip has no secondary line per option, and the fork-policy group is domain-bound with
// hard-coded options; this atom generalizes both into a reusable card radiogroup. See spec Reuse
// Audit (arc-editor-canvas-v1.md).
import React from 'react';
import GlyphAtom from '../Glyph/GlyphAtom';
import './ChoiceCardGroupAtom.scss';

export interface ChoiceCardOption {
  value: string;
  label: string;
  note?: string;
  /** Iconify `lucide:*` name. Ladder options fall back to hand/zap from `held` when omitted. */
  icon?: string;
  disabled?: boolean;
  /**
   * Marks the option as "held for a person" rather than running on its own — the ladder
   * variant's rung (`arc-inspector.jsx` `TierLadder`'s `acHoldsForPerson`). Drives the
   * hand/zap icon fallback and a `data-holds` attribute for anyone styling the distinction.
   */
  held?: boolean;
  /** Parity self-description for this option — see visual-fidelity.md. */
  datum?: string;
  /** Overrides the default `choice-card` parity kind — a contract decomposed before this atom
   * existed can classify the option's source element as plain `button` (arc-editor-canvas-v1's
   * `side.button`, `arc-inspector.jsx:211`). Only read when `datum` is set. */
  datumKind?: string;
  /** Parity self-description for the option's own bold label span, when the contract tracks it
   * as a separate datum from the card (arc-editor-canvas-v1's `canvas.s-label`/`one-stage#2`). */
  labelDatum?: string;
}

export interface ChoiceCardGroupAtomProps {
  options: ChoiceCardOption[];
  value: string | null;
  onChange: (value: string) => void;
  /** Accessible name for the radiogroup — required, since no visible group title is guaranteed. */
  ariaLabel: string;
  /** `default` is a list of cards; `ladder` is an ordered vertical list with a held/automatic rung. */
  variant?: 'default' | 'ladder';
  /**
   * How the `default` variant lays its cards out: `stacked` (one full-width row per option — the
   * inspector rail's groups) or `inline` (a centred row that wraps, each card sized to its copy —
   * the canvas starters). The ladder is always stacked.
   */
  layout?: 'stacked' | 'inline';
  disabled?: boolean;
  className?: string;
  'data-datum'?: string;
  'data-kind'?: string;
  'data-testid'?: string;
}

const ChoiceCardGroupAtom: React.FC<ChoiceCardGroupAtomProps> = ({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'default',
  layout = 'stacked',
  disabled = false,
  className = '',
  'data-datum': dataDatum,
  'data-kind': dataKind,
  'data-testid': testId,
}) => {
  const enabled = options.filter((option) => !option.disabled && !disabled);
  /** The one card that sits in the tab order: the selected card, or — with nothing selected
   * yet (a starter picker on an empty arc) — the first enabled card, so the group is still
   * reachable by keyboard (ARIA radiogroup roving tabindex; ux-craft A1). */
  const tabStop = enabled.some((option) => option.value === value) ? value : enabled[0]?.value ?? null;

  /** Roving focus across the radiogroup — arrow keys move the selection, as ARIA requires. */
  const moveSelection = (from: string, delta: number, current: HTMLElement) => {
    if (enabled.length === 0) return;
    const index = enabled.findIndex((option) => option.value === from);
    const nextIndex = (((index < 0 ? 0 : index) + delta) % enabled.length + enabled.length) % enabled.length;
    const next = enabled[nextIndex];
    if (!next) return;
    onChange(next.value);
    current.parentElement
      ?.querySelectorAll<HTMLElement>('[role="radio"]:not([disabled])')
      ?.[nextIndex]?.focus();
  };

  const classes = [
    'choice-card-group',
    `choice-card-group--${variant}`,
    variant === 'default' && layout === 'inline' ? 'choice-card-group--inline' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      data-datum={dataDatum}
      data-kind={dataDatum ? (dataKind ?? 'choice-card-group') : undefined}
    >
      {options.map((option) => {
        const selected = value === option.value;
        const isDisabled = disabled || option.disabled;
        const icon = option.icon ?? (variant === 'ladder' ? (option.held ? 'lucide:hand' : 'lucide:zap') : undefined);

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={isDisabled ? undefined : option.value === tabStop ? 0 : -1}
            disabled={isDisabled}
            data-on={selected ? '1' : undefined}
            data-holds={option.held ? '1' : undefined}
            className={`choice-card-group__card ${selected ? 'choice-card-group__card--selected' : ''} ${isDisabled ? 'choice-card-group__card--disabled' : ''}`}
            data-datum={option.datum}
            data-container="each(options)"
            data-kind={option.datum ? (option.datumKind ?? 'choice-card') : undefined}
            onClick={() => !isDisabled && onChange(option.value)}
            onKeyDown={(event) => {
              if (isDisabled) return;
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                moveSelection(option.value, 1, event.currentTarget);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                moveSelection(option.value, -1, event.currentTarget);
              }
            }}
          >
            <span className="choice-card-group__card-head">
              {icon && (
                <GlyphAtom glyph={icon} aria-hidden className="choice-card-group__card-icon" />
              )}
              <span
                className="choice-card-group__card-label"
                data-datum={option.labelDatum}
                data-kind={option.labelDatum ? 'strong' : undefined}
              >
                {option.label}
              </span>
            </span>
            {option.note && <span className="choice-card-group__card-note">{option.note}</span>}
          </button>
        );
      })}
    </div>
  );
};

ChoiceCardGroupAtom.displayName = 'ChoiceCardGroupAtom';

export default ChoiceCardGroupAtom;
